import cn from 'classnames'
import React, { useCallback } from 'react'

import type { IKitControlProps, IOption, KitSize } from 'components/uiKit/KitTypes'
import { isMixed } from 'components/uiKit/KitUtils'
import { testProps } from 'utils/test/qaData'

import * as s from './Checkbox.module.scss'
import type { CheckboxDefaultType } from './CheckboxItem'
import CheckboxItem from './CheckboxItem'

export type CheckboxValue = string | number | string[] | boolean
export type ArrayElement<A> = A extends (infer T)[] ? T : A

export interface ICheckboxOption<T extends CheckboxValue> {
  label?: React.ReactNode | React.FC
  valueTrue: T
  valueFalse: T
}

export interface ICheckboxProps<T extends CheckboxValue>
  extends CheckboxDefaultType,
    IKitControlProps<T> {
  option?: ICheckboxOption<T>
  options?: IOption<ArrayElement<T>>[]
  hideCheckbox?: boolean
  children?: React.ReactNode | string | null
  indeterminate?: boolean
  direction?: 'vertical' | 'horizontal'
  testData?: string
  size?: KitSize
  error?: boolean
  activeColor?: string
  bordered?: boolean
  info?: React.ReactNode
}

const Checkbox = <T extends CheckboxValue>(
  {
    name,
    onChange,
    value: rawValue,
    option,
    options,
    children,
    hideCheckbox,
    disabled,
    indeterminate,
    direction = 'horizontal',
    testData,
    error,
    activeColor,
    bordered,
    info,
    ...rest
  }: ICheckboxProps<T>,
  ref: React.Ref<HTMLLabelElement>,
) => {
  const value = isMixed(rawValue) ? undefined : (rawValue as T | undefined)
  const effectiveIndeterminate = indeterminate || isMixed(rawValue)
  const onChangeWrapper = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value: checkboxValue, checked } = event.target
      if (option) {
        return onChange?.(value === option.valueTrue ? option.valueFalse : option.valueTrue)
      } else if (options) {
        const typedValue = (value || []) as string[]
        const newValue = checked
          ? [...typedValue, checkboxValue]
          : typedValue.filter((item) => item !== checkboxValue)

        return onChange?.(newValue as T)
      } else {
        return onChange?.(!value as T)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, value, options],
  )

  if (options) {
    return (
      <div
        className={cn(s.root, s[direction], { [s.bordered]: bordered })}
        ref={ref as React.Ref<HTMLDivElement>}
        {...testProps({ el: 'checkbox', name, testData })}
      >
        {options.map((item) => (
          <CheckboxItem
            {...rest}
            activeColor={activeColor}
            bordered={bordered}
            disabled={disabled}
            hideCheckbox={hideCheckbox}
            indeterminate={effectiveIndeterminate}
            item={item}
            key={String(item.value)}
            name={name}
            onChange={onChangeWrapper}
            value={value}
          />
        ))}
      </div>
    )
  }

  return (
    <CheckboxItem
      {...rest}
      activeColor={activeColor}
      bordered={bordered}
      disabled={disabled}
      hideCheckbox={hideCheckbox}
      indeterminate={effectiveIndeterminate}
      info={info}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      item={{ label: children, value: value as any }}
      name={name}
      onChange={onChangeWrapper}
      option={option}
      ref={ref}
      testData={testData}
      value={value}
    />
  )
}

export default React.forwardRef(Checkbox) as <T extends CheckboxValue>(
  props: ICheckboxProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement
